import { FC, Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { snakeToCamel } from "caseparser";
import Color from "color";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import { router } from "@/app/app.router";
import {
  selectPermissions,
  selectAccessToken,
  setAccessToken,
  selectUser,
  setUser,
} from "@/app/auth/auth.slice";
import { useSessionQuery } from "./auth/auth.api";

import { echo } from "@/echo";
import { toasts } from "@/shared/utils/toasts";
import { addMessage, updateConversations } from "./chat/chat.slice";

export const App: FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const accessToken = useSelector(selectAccessToken);
  const [userAuthenticated, setUserAuthenticated] = useState(false);

  const sessionQuery = useSessionQuery({
    accessToken,
  });

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    dispatch(setAccessToken({ accessToken }));
    sessionQuery.refetch();
  }, []);

  useEffect(() => {
    if (!user) return;
    echo
      .channel(`App.Conversation.${user.id}`)
      .listen("ConversationMessage", (event: any) => {
        console.debug(event);
      });
    return () => {
      echo
        .channel(`App.Conversation.${user.id}`)
        .stopListening("ConversationMessage");
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    echo
      .channel(`App.Notification.${user.id}`)
      .listen("NotificationMessage", (event: any) => {
        console.debug(event);
      });
    return () => {
      echo
        .channel(`App.Notification.${user.id}`)
        .stopListening("NotificationMessage");
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const chanel = `App.Chat.${user.id}`;
    const echoChanel = echo.channel(chanel);

    echoChanel.listen("ChatMessage", (event: any) => {
      const payload = snakeToCamel(event);

      const searchParams = new URLSearchParams(window.location.search);

      if (payload.senderId !== user.id) {
        console.log(payload);
        if (searchParams.get("support") == "1") {
          toasts.message(
            payload.client.name,
            payload.chateable.content,
            payload.client.avatar
          );
        } else {
          toasts.message(
            payload.manager.name,
            payload.chateable.content,
            payload.manager.avatar
          );
        }
      }

      dispatch(updateConversations({ message: payload, user }));
      dispatch(addMessage({ message: payload }));
    });
    return () => {
      echoChanel.stopListening("ChatMessage");
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (user) {
      document.documentElement.style.setProperty(
        "--primary",
        user.branch.firstColor
      );
      document.documentElement.style.setProperty(
        "--primary-50",
        Color(user.branch.firstColor).lighten(0.5).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-100",
        Color(user.branch.firstColor).lighten(0.4).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-200",
        Color(user.branch.firstColor).lighten(0.3).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-300",
        Color(user.branch.firstColor).lighten(0.2).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-400",
        Color(user.branch.firstColor).lighten(0.1).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-500",
        user.branch.firstColor
      );
      document.documentElement.style.setProperty(
        "--primary-600",
        Color(user.branch.firstColor).darken(0.1).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-700",
        Color(user.branch.firstColor).darken(0.2).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-800",
        Color(user.branch.firstColor).darken(0.3).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-900",
        Color(user.branch.firstColor).darken(0.4).hex()
      );
      document.documentElement.style.setProperty(
        "--primary-950",
        Color(user.branch.firstColor).darken(0.5).hex()
      );

      document.documentElement.style.setProperty(
        "--secondary",
        user.branch.secoundColor
      );
      document.documentElement.style.setProperty(
        "--secondary-50",
        Color(user.branch.secoundColor).lighten(0.5).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-100",
        Color(user.branch.secoundColor).lighten(0.4).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-200",
        Color(user.branch.secoundColor).lighten(0.3).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-300",
        Color(user.branch.secoundColor).lighten(0.2).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-400",
        Color(user.branch.secoundColor).lighten(0.1).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-500",
        user.branch.secoundColor
      );
      document.documentElement.style.setProperty(
        "--secondary-600",
        Color(user.branch.secoundColor).darken(0.1).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-700",
        Color(user.branch.secoundColor).darken(0.2).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-800",
        Color(user.branch.secoundColor).darken(0.3).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-900",
        Color(user.branch.secoundColor).darken(0.4).hex()
      );
      document.documentElement.style.setProperty(
        "--secondary-950",
        Color(user.branch.secoundColor).darken(0.5).hex()
      );
    }
  }, [user]);

  useEffect(() => {
    if (sessionQuery.isSuccess) {
      dispatch(setUser({ user: sessionQuery.data }));
      setUserAuthenticated(true);
    }

    if (sessionQuery.isError) {
      setUserAuthenticated(false);
    }
  }, [sessionQuery]);

  // Verificamos que los permisos ya se hallan cargado
  const permissions = useSelector(selectPermissions);

  return (
    <Fragment>
      {userAuthenticated ? (
        permissions ? (
          <RouterProvider router={router}></RouterProvider>
        ) : (
          <div className="flex justify-center items-center h-screen w-screen">
            <FontAwesomeIcon icon={faSpinner} spin className="text-6xl" />
          </div>
        )
      ) : (
        <RouterProvider router={router}></RouterProvider>
      )}
    </Fragment>
  );
};

export default App;
